package org.moxultimate.topwithmox.apps.api

import io.ktor.client.*
import io.ktor.client.request.*
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import org.moxultimate.topwithmox.apps.data.AppsResponse
import org.moxultimate.topwithmox.apps.data.AppsTagResponse
import org.moxultimate.topwithmox.apps.data.AppsTypeResponse
import org.moxultimate.topwithmox.core.base.BaseAPI.baseAPI
import org.moxultimate.topwithmox.core.handler.APIHandler
import org.moxultimate.topwithmox.core.state.APIState

/**
 * AppsRepository
 * Handles all read operations for apps, tags, and types.
 * Write operations (create, update, delete, saveTag, saveType) live in AppsCRUDRepository.
 *
 * @param httpClient Ktor HttpClient injected via Koin.
 */
class AppsRepository(
    private val httpClient: HttpClient,
) {
    private val baseUrl = "$baseAPI/apps"
    private val tagUrl  = "$baseAPI/apps-tag"
    private val typeUrl = "$baseAPI/apps-type"

    /**
     * getApps
     * Fetches a page of apps using cursor-based pagination (20 items, newest first).
     * Pass [beforeId] = Long.MAX_VALUE to load the first page.
     *
     * @param beforeId Pagination cursor — loads items with id strictly less than this value.
     * @return Flow emitting Loading → Success(List<AppsResponse>) or Error.
     */
    fun getApps(beforeId: Long = Long.MAX_VALUE): Flow<APIState<List<AppsResponse>>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.get(baseUrl) {
                parameter("beforeId", beforeId)
            }
        })
    }

    /**
     * getAppById
     * Fetches a single app entry via GET /apps/{id}.
     *
     * @param id App ID to fetch.
     * @return Flow emitting Loading → Success(AppsResponse) or Error.
     */
    fun getAppById(id: Long): Flow<APIState<AppsResponse>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall { httpClient.get("$baseUrl/$id") })
    }

    /**
     * getAllTags
     * Fetches all app tags from GET /apps-tag.
     *
     * @return Flow emitting Loading → Success(List<String>) or Error.
     */
    fun getAllTags(): Flow<APIState<List<String>>> = flow {
        emit(APIState.Loading)
        emit(
            APIHandler.safeApiCall<List<AppsTagResponse>, List<String>>(
                apiToBeCalled = { httpClient.get(tagUrl) },
                transformer = { entities -> entities.mapNotNull { it.tag }.distinct() },
            )
        )
    }

    /**
     * getAllTypes
     * Fetches all app types from GET /apps-type. Used to populate the type filter chips.
     *
     * @return Flow emitting Loading → Success(List<String>) or Error.
     */
    fun getAllTypes(): Flow<APIState<List<String>>> = flow {
        emit(APIState.Loading)
        emit(
            APIHandler.safeApiCall<List<AppsTypeResponse>, List<String>>(
                apiToBeCalled = { httpClient.get(typeUrl) },
                transformer = { entities -> entities.mapNotNull { it.type }.distinct() },
            )
        )
    }
}
